import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './Checkout.css';

function Checkout({ setCurrentSection, setShowAuthModal }) {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [authChosen, setAuthChosen] = useState(user ? true : false); // Track if user has made auth choice
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    city: '',
    zipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-fill form if user is logged in
  useEffect(() => {
    if (user) {
      setAuthChosen(true);
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
      }));
    }
  }, [user]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignInOption = () => {
    setShowAuthModal(true);
  };

  const handleGuestCheckoutOption = async () => {
    setAuthChosen(true);
  };

  const handlePaystackPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Initialize Paystack payment
      const email = formData.email;
      const amount = Math.round(getTotalPrice() * 100); // Paystack expects amount in cents
      const reference = `AFRO-${Date.now()}`;

      // Create inline handler
      const handler = window.PaystackPop.setup({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: email,
        amount: amount,
        ref: reference,
        currency: 'USD',
        onClose: function() {
          setLoading(false);
          setError('Payment window closed');
        },
        onSuccess: function(response) {
          handlePaymentSuccess('Paystack', response);
        }
      });

      handler.openIframe();
    } catch (err) {
      setError('Failed to initialize Paystack payment');
      setLoading(false);
    }
  };

  const handleStripePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: getTotalPrice(),
          email: formData.email,
          orderData: { cartItems, formData }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        handlePaymentSuccess('Stripe', data);
      } else {
        setError(data.message || 'Payment failed');
      }
    } catch (err) {
      setError('Error processing Stripe payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/create-paypal-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: getTotalPrice(),
          email: formData.email,
          orderData: { cartItems, formData }
        })
      });

      const data = await response.json();
      
      if (data.id) {
        // Redirect to PayPal approval URL
        window.location.href = data.approve_url;
      } else {
        setError(data.message || 'Failed to create PayPal order');
      }
    } catch (err) {
      setError('Error processing PayPal payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (method, response) => {
    setSuccess(true);
    clearCart();
    
    // Log order (in production, send to backend)
    console.log(`Order placed successfully via ${method}`, {
      response,
      formData,
      items: cartItems,
      total: getTotalPrice()
    });

    // Redirect to confirmation after 3 seconds
    setTimeout(() => {
      setCurrentSection('home');
    }, 3000);
  };

  if (cartItems.length === 0) {
    return (
      <div className="checkout-section">
        <div className="empty-checkout">
          <h2>No Items to Checkout</h2>
          <p>Your cart is empty. Please add items before checking out.</p>
          <button 
            className="back-shopping-btn"
            onClick={() => setCurrentSection('wrappers')}
          >
            Back to Shopping
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="checkout-section">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>Order Placed Successfully!</h2>
          <p>Thank you for your purchase.</p>
          <p>A confirmation email has been sent to {formData.email}</p>
          <p className="redirect-message">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  // Show auth choice first if not chosen yet
  if (!authChosen) {
    return (
      <div className="checkout-section">
        <div className="auth-choice-container">
          <div className="auth-choice-content">
            <h2>📦 Ready to Complete Your Order?</h2>
            <p>Choose how you'd like to continue</p>
            
            <div className="auth-choice-buttons">
              <button 
                className="choice-btn signin-choice"
                onClick={handleSignInOption}
              >
                <div className="choice-icon">👤</div>
                <div className="choice-text">
                  <h3>Sign In</h3>
                  <p>Use your existing account or create a new one</p>
                </div>
              </button>

              <button 
                className="choice-btn guest-choice"
                onClick={handleGuestCheckoutOption}
              >
                <div className="choice-icon">🚀</div>
                <div className="choice-text">
                  <h3>Continue as Guest</h3>
                  <p>Quick checkout without an account</p>
                </div>
              </button>
            </div>

            <button 
              className="back-to-cart-btn"
              onClick={() => setCurrentSection('cart')}
            >
              ← Back to Cart
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-section">
      <div className="checkout-container">
        <div className="checkout-form">
          <h2>Checkout</h2>

          <form>
            <h3>Shipping Information</h3>
            <div className="form-row">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                required
                value={formData.firstName}
                onChange={handleFormChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                required
                value={formData.lastName}
                onChange={handleFormChange}
              />
            </div>

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              required
              value={formData.email}
              onChange={handleFormChange}
            />

            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              required
              value={formData.phone}
              onChange={handleFormChange}
            />

            <input
              type="text"
              name="address"
              placeholder="Street Address"
              required
              value={formData.address}
              onChange={handleFormChange}
            />

            <div className="form-row">
              <input
                type="text"
                name="city"
                placeholder="City"
                required
                value={formData.city}
                onChange={handleFormChange}
              />
              <input
                type="text"
                name="zipCode"
                placeholder="ZIP Code"
                required
                value={formData.zipCode}
                onChange={handleFormChange}
              />
            </div>

            <input
              type="text"
              name="country"
              placeholder="Country"
              required
              value={formData.country}
              onChange={handleFormChange}
            />

            <h3>Payment Method</h3>
            <div className="payment-methods">
              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="bank_transfer"
                  checked={paymentMethod === 'bank_transfer'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>🏦 Bank Transfer</span>
              </label>

              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="opay"
                  checked={paymentMethod === 'opay'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>💜 OPay Wallet</span>
              </label>

              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>💳 Visa / Mastercard</span>
              </label>

              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="stripe"
                  checked={paymentMethod === 'stripe'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>🔘 Stripe</span>
              </label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="button"
              className="payment-btn"
              disabled={loading || !formData.firstName || !formData.email || !formData.address}
              onClick={
                paymentMethod === 'stripe'
                  ? handleStripePayment
                  : paymentMethod === 'card'
                  ? handlePaystackPayment
                  : paymentMethod === 'bank_transfer' || paymentMethod === 'opay'
                  ? (e) => {
                      e.preventDefault();
                      setLoading(true);
                      setTimeout(() => {
                        handlePaymentSuccess(paymentMethod === 'bank_transfer' ? 'Bank Transfer' : 'OPay', {
                          reference: `AFRO-${Date.now()}`,
                        });
                      }, 800);
                    }
                  : handlePayPalPayment
              }
            >
              {loading ? 'Processing...' : `Pay ₦${getTotalPrice().toFixed(2)} with ${paymentMethod === 'bank_transfer' ? 'Bank Transfer' : paymentMethod === 'opay' ? 'OPay' : paymentMethod === 'card' ? 'Visa / Mastercard' : paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}
            </button>

            <button
              type="button"
              className="back-btn"
              onClick={() => setCurrentSection('cart')}
              disabled={loading}
            >
              Back to Cart
            </button>
          </form>
        </div>

        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="summary-items">
            {cartItems.map(item => (
              <div key={item.id} className="summary-item">
                <span>{item.name} x {item.quantity}</span>
                <span>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="summary-total">
            <span>Total:</span>
            <span>${getTotalPrice().toFixed(2)}</span>
          </div>

          <div className="security-badge">
            🔒 Secure Payment
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
